import { licensePlateSchemaObject, locationBaseSchema, zo } from 'remote/global';
import { fileTypeObject } from '../types';
import { transformFloatDecimalSeparator, transformToSingleDecimal } from '../helpers';
import { rideExtrasSchemaObject } from '../RideFormFactory/types';

// TODO: Make empty entries send "Kohustuslik" instead of "Ebakorrektne sisend"
const vehicleDetailsSchema = zo.object({
  licensePlate: licensePlateSchemaObject,
  make: zo.string().min(1).max(32),
  model: zo.string().min(1).max(32),
  year: zo.coerce
    .string()
    .min(1)
    .pipe(zo.coerce.number().int().gte(1975).lte(new Date().getFullYear())),
  lengthMeters: zo.coerce
    .string()
    .min(1)
    .transform(transformFloatDecimalSeparator)
    .pipe(zo.coerce.number().gte(6).lte(16).transform(transformToSingleDecimal)),
  mileageKm: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(0).lte(5_000_000)),
  seats: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(6).lte(95)),
  standingPlaces: zo.coerce.string().min(1).pipe(zo.coerce.number().int().gte(0).lte(100)),
});
type VehicleDetailsSchemaType = zo.infer<typeof vehicleDetailsSchema>;

const vehicleExtrasSchema = zo.object({
  availableForOffer: zo.coerce.boolean(), // we coerce because default is undefined
  // TODO: Fix this issue with errors showing at incorrect checkbox indexes
  // TODO: Fix this any check
  extras: rideExtrasSchemaObject,
  kidFriendly: zo.boolean(),
  acceptInternationalRides: zo.boolean(),
});
type VehicleExtrasSchemaType = zo.infer<typeof vehicleExtrasSchema>;

const vehicleImageSchema = zo
  .object({
    attachments: zo.array(fileTypeObject).optional(),
    isMarkedAsActive: zo.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    // throw validation error if vehicle is marked as active and no images are attached
    if (data.isMarkedAsActive && (!data.attachments || data.attachments?.length === 0)) {
      ctx.addIssue({
        code: zo.ZodIssueCode.custom,
        params: {
          i18n: {
            key: 'minimumOnePictureNeeded',
          },
        },
        path: ['attachments'],
      });
    }
  });
type VehicleImageSchemaType = zo.infer<typeof vehicleImageSchema>;

const vehicleDocumentsSchema = zo.object({
  insuranceDocuments: zo.array(fileTypeObject).optional(),
  vehicleCards: zo.array(fileTypeObject).optional(),
});
type VehicleDocumentsSchemaType = zo.infer<typeof vehicleDocumentsSchema>;

type VehicleLocationItemSchemaType = zo.infer<typeof locationBaseSchema>;

const vehicleDefaultLocationSchema = zo.object({
  defaultLocation: locationBaseSchema,
});
type VehicleDefaultLocationSchemaType = zo.infer<typeof vehicleDefaultLocationSchema>;

const vehicleLocationSchema = zo.object({
  location: locationBaseSchema,
});
type VehicleLocationSchemaType = zo.infer<typeof vehicleLocationSchema>;

const vehicleBaseLocationSchema = zo.object({
  location: locationBaseSchema,
  // TODO: id of number can change to string of auto-gen uuid
  id: zo.number().or(zo.string()),
  validFrom: zo.string().datetime(),
  validTo: zo.string().datetime(),
});
type VehicleBaseLocationSchemaType = zo.infer<typeof vehicleBaseLocationSchema>;

const vehicleLocationListSchema = zo.object({
  locations: zo.array(vehicleBaseLocationSchema),
});
type VehicleLocationListSchemaType = zo.infer<typeof vehicleLocationListSchema>;

const vehicleDriverSchema = zo.object({
  defaultDriverId: zo.number().or(zo.null()),
});
type VehicleDriverSchemaType = zo.infer<typeof vehicleDriverSchema>;

const vehiclePayloadSchema = vehicleDetailsSchema
  .and(vehicleExtrasSchema)
  .and(vehicleDefaultLocationSchema);
type VehiclePayload = zo.infer<typeof vehiclePayloadSchema>;

export {
  vehicleDefaultLocationSchema,
  vehicleExtrasSchema,
  vehicleImageSchema,
  vehicleLocationListSchema,
  vehicleLocationSchema,
  vehicleDetailsSchema,
  vehicleDocumentsSchema,
  vehicleDriverSchema,
  locationBaseSchema,
  vehiclePayloadSchema,
};
export type {
  VehicleBaseLocationSchemaType,
  VehicleDefaultLocationSchemaType,
  VehicleExtrasSchemaType,
  VehicleImageSchemaType,
  VehicleLocationItemSchemaType,
  VehicleLocationListSchemaType,
  VehicleLocationSchemaType,
  VehicleDetailsSchemaType,
  VehicleDocumentsSchemaType,
  VehicleDriverSchemaType,
  VehiclePayload,
};
